/**
 * Auto-enhances every `.history-tabs` segmented control with a single
 * sliding pill indicator, so toggling `.active` between buttons animates
 * the pill instead of jump-cutting. Works without any callsite changes —
 * the JSX stays as plain `<button class="history-tab[ active]">…</button>`.
 *
 * Mechanism: a MutationObserver on `document.body` discovers tracks as
 * they mount/unmount. Each track gets its own observer that watches for
 * `.active` class flips on descendants and child-list changes; on every
 * update we measure the active button's `offsetLeft / offsetWidth` and
 * publish them as `--tab-indicator-x` / `--tab-indicator-w` on the track.
 * The CSS `::before` reads those vars and CSS transitions them.
 *
 * The first measurement also sets `data-tabs-ready="true"` (one frame
 * later, via rAF), which lets the CSS swap the legacy `.active` pill bg
 * for the sliding indicator without a "pop-in" animation on first paint.
 */

const TRACK_SELECTOR = ".history-tabs";
const ACTIVE_SELECTOR = ".history-tab.active";
const READY_ATTR = "data-tabs-ready";

function measure(track: HTMLElement) {
  const active = track.querySelector<HTMLElement>(ACTIVE_SELECTOR);
  if (!active) {
    track.removeAttribute(READY_ATTR);
    return;
  }
  track.style.setProperty("--tab-indicator-x", `${active.offsetLeft}px`);
  track.style.setProperty("--tab-indicator-w", `${active.offsetWidth}px`);
  if (!track.hasAttribute(READY_ATTR)) {
    // Defer the ready flag by one frame so the CSS transition only kicks
    // in for subsequent updates — keeps the first paint static (no slide
    // from x=0 on mount).
    requestAnimationFrame(() => track.setAttribute(READY_ATTR, "true"));
  }
}

function attachTrack(track: HTMLElement): () => void {
  measure(track);
  const update = () => measure(track);

  const mo = new MutationObserver(update);
  mo.observe(track, {
    attributes: true,
    attributeFilter: ["class"],
    childList: true,
    subtree: true,
  });

  const ro = new ResizeObserver(update);
  ro.observe(track);
  for (const child of Array.from(track.children)) ro.observe(child);

  return () => {
    mo.disconnect();
    ro.disconnect();
  };
}

export function installTabIndicator(): () => void {
  const cleanups = new WeakMap<Element, () => void>();

  const attach = (el: HTMLElement) => {
    if (cleanups.has(el)) return;
    cleanups.set(el, attachTrack(el));
  };
  const detach = (el: Element) => {
    const fn = cleanups.get(el);
    if (fn) {
      fn();
      cleanups.delete(el);
    }
  };

  document.querySelectorAll<HTMLElement>(TRACK_SELECTOR).forEach(attach);

  const docMo = new MutationObserver((muts) => {
    for (const m of muts) {
      m.addedNodes.forEach((n) => {
        if (!(n instanceof HTMLElement)) return;
        if (n.matches(TRACK_SELECTOR)) attach(n);
        n.querySelectorAll<HTMLElement>(TRACK_SELECTOR).forEach(attach);
      });
      m.removedNodes.forEach((n) => {
        if (!(n instanceof HTMLElement)) return;
        if (n.matches(TRACK_SELECTOR)) detach(n);
        n.querySelectorAll(TRACK_SELECTOR).forEach(detach);
      });
    }
  });
  docMo.observe(document.body, { childList: true, subtree: true });

  return () => {
    docMo.disconnect();
    document.querySelectorAll(TRACK_SELECTOR).forEach(detach);
  };
}
